/**
 * ObondoPaywall — shared block-out shown to individual-tier users on
 * both `/app/assistant` and `/app/coach`. Same visual grammar as the
 * Expert Charts banner so upgrade prompts feel unified.
 *
 * Free users can navigate to Obondo (the nav item is now visible in
 * every tier) but hit this screen instead of the real assistant.
 * Advanced + institutional tiers bypass this entirely.
 */
import { Link } from "react-router-dom";
import { Sparkles, Lock, MessageSquareText, BarChart3, Bell } from "lucide-react";

const _FEATURES = [
    { Icon: MessageSquareText, label: "Conversational analysis",  copy: "Ask Obondo about your open trades, market moves, or a symbol's setup." },
    { Icon: BarChart3,         label: "Chart-driven answers",     copy: "Obondo renders inline charts, tables, and portfolio metrics on demand." },
    { Icon: Bell,              label: "Proactive trade signals",  copy: "Streamed watchlist alerts and news deltas — pushed to your inbox as they fire." },
];

export default function ObondoPaywall({ product = "Obondo Assistant" }) {
    return (
        <div className="max-w-3xl mx-auto py-10 md:py-16" data-testid="obondo-paywall">
            <div className="border-2 border-primary/40 rounded-2xl bg-gradient-to-br from-primary/10 via-transparent to-transparent p-6 md:p-10">
                <div className="flex items-start gap-4 mb-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary text-primary-foreground flex items-center justify-center flex-shrink-0">
                        <Sparkles className="w-7 h-7" />
                    </div>
                    <div>
                        <div className="text-[10px] uppercase font-bold tracking-wider text-primary mb-1 flex items-center gap-1.5">
                            <Lock className="w-3 h-3" /> Advanced Trader feature
                        </div>
                        <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight">
                            Unlock {product}
                        </h1>
                        <p className="text-sm text-muted-foreground mt-1">
                            Your AI co-pilot for Pan-African + global markets — real-time analysis, chart generation, and proactive signals wired directly into your terminal.
                        </p>
                    </div>
                </div>

                <div className="grid sm:grid-cols-3 gap-3 mb-8">
                    {_FEATURES.map(({ Icon, label, copy }, i) => (
                        <div key={label} className="rounded-lg border border-border bg-card/60 p-3" data-testid={`obondo-paywall-feature-${i}`}>
                            <Icon className="w-5 h-5 text-primary mb-2" />
                            <div className="text-sm font-bold">{label}</div>
                            <p className="text-xs text-muted-foreground mt-1">{copy}</p>
                        </div>
                    ))}
                </div>

                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                    <Link
                        to="/app/subscription"
                        data-testid="obondo-paywall-upgrade-btn"
                        className="inline-flex items-center gap-2 h-11 px-5 rounded-lg bg-primary text-primary-foreground font-bold text-sm hover:brightness-110"
                    >
                        <Sparkles className="w-4 h-4" /> Upgrade to Advanced · KES 1,000/mo
                    </Link>
                    <p className="text-xs text-muted-foreground">
                        Or included in every Institutional (MoE / CMA) plan.
                    </p>
                </div>
            </div>
        </div>
    );
}
